/*
  Save Version Notes Modal

  Modal for entering version notes when saving a journey.
*/

import React, { useState } from 'react';
import {
  Modal,
  Button,
  TextArea,
  Checkbox,
} from '@usealloy/component-library';
import styled from 'styled-components';

const NotesContainer = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  color: #374151;
`;

const CharacterCount = styled.div`
  font-size: 12px;
  color: #6b7280;
  text-align: right;
  margin-top: 4px;
`;

const CheckboxContainer = styled.div`
  margin-top: 24px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 6px;
`;

const WarningText = styled.p`
  font-size: 14px;
  color: #dc2626;
  margin-top: 8px;
`;

const InfoText = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 16px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
  border-left: 3px solid #3b82f6;
`;

interface SaveVersionNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (notes: string, setAsActive: boolean) => void;
  isDraft?: boolean;
}

const SaveVersionNotesModal: React.FC<SaveVersionNotesModalProps> = ({
  isOpen,
  onClose,
  onSave,
  isDraft = false,
}) => {
  const [notes, setNotes] = useState('');
  const [setAsActive, setSetAsActive] = useState(false);
  const maxLength = 500;

  const handleSave = () => {
    onSave(notes.trim(), setAsActive);
  };

  // Determine header text based on draft status
  const headerText = isDraft ? 'Save Draft' : 'Save as New Version';
  const saveButtonText = isDraft ? 'Save Draft' : 'Save New Version';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header={headerText}
      secondaryAction={(
        <Button onClick={onClose}>
          Cancel
        </Button>
      )}
      primaryAction={(
        <Button onClick={handleSave}>
          {saveButtonText}
        </Button>
      )}
      hasCloseIcon={false}
    >
      {isDraft ? (
        <InfoText>
          This will update the existing draft without creating a new version.
        </InfoText>
      ) : (
        <InfoText>
          This will create a new version in the version history.
        </InfoText>
      )}
      <NotesContainer>
        <Label htmlFor="version-notes">
          Version Notes
        </Label>
        <TextArea
          id="version-notes"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder={
            isDraft
              ? 'Add notes about this draft...'
              : 'Describe what changed in this version...'
          }
          rows={4}
          maxLength={maxLength}
          style={{ width: '100%' }}
        />
        <CharacterCount>
          {notes.length} / {maxLength}
        </CharacterCount>
      </NotesContainer>

      <CheckboxContainer>
        <Checkbox
          checked={setAsActive}
          onChange={e => setSetAsActive(e.target.checked)}
          label="Set as Active Version"
        />
        {setAsActive && (
          <WarningText>
            This will replace the currently active version and affect live traffic.
          </WarningText>
        )}
      </CheckboxContainer>
    </Modal>
  );
};

export default SaveVersionNotesModal;

